import BaseApiRepository from '../_common/BaseApiRepository';
// eslint-disable-next-line no-unused-vars
import TemplateRequestDto from './Dto/TemplateRequestDto';
import TemplateVariablesDto from './Dto/TemplateVariablesDto';
import TemplateDto from './Dto/TemplateDto';
import PageTemplateDto from './Dto/PageTemplateDto';
import TemplateVariablesResponseDto from './Dto/TemplateVariablesResponseDto';

import CheckoutSchedulerDto from './Dto/CheckoutSchedulerDto';

class TemplatesApiRepository extends BaseApiRepository {
    /**
     * Get template details
     * @param {string} templateId - Template ID
     * @returns {Promise<Template>}
     */
    async getTemplate(templateId) {
        const response = await this.apiRequest('GET', `template/${templateId}`);
        return new TemplateDto.Builder()
            .withUuid(response.data.uuid)
            .withAccountId(response.data.account_id)
            .withBody(response.data.body)
            .withHeader(response.data.header)
            .withChannelType(response.data.channel_type)
            .withTemplateName(response.data.template_name)
            .withLanguage(response.data.language)
            .withCategory(response.data.category)
            .withStatus(response.data.status)
            .withScore(response.data.score)
            .withType(response.data.type)
            .withTemplateType(response.data.template_type)
            .withFlowType(response.data.flow_type)
            .withSkillId(response.data.skill_id)
            .withInternalTemplateId(response.data.internal_template_id)
            .withTemplateParameters(response.data.template_parameters)
            .withExtraParameters(response.data.extra_parameters)
            .withIssues(response.data.issues)
            .withModifyBy(response.data.modify_by)
            .withBusinessAccountId(response.data.business_account_id)
            .withDateCreated(response.data.date_created)
            .withDateUpdated(response.data.date_updated)
            .withVariables(response.data.variables)
            .withButtons(
                response.data.buttons
                    ? response.data.buttons.map((btn) => {
                          if (btn.type === 'URL' && btn.url && btn.url.endsWith('{{1}}')) {
                              return { ...btn, url: '{{url_1}}', url_type: 'DYNAMIC' };
                          }
                          return btn;
                      })
                    : [],
            )
            .build();
    }

    /**
     * Update template
     * @param {string} templateId - Template ID
     * @param {TemplateRequestDto} request - Template request data
     * @returns {Promise<Object>}
     */
    async updateTemplate(templateId, request) {
        const response = await this.apiRequest('PUT', `template/${templateId}`, request.toJSON());
        return response;
    }

    /**
     * Delete template
     * @param {string} templateId - Template ID
     * @param {TemplateRequestDto} request - Template request data
     * @returns {Promise<Object>}
     */
    async deleteTemplate(templateId, request) {
        const response = await this.apiRequest('DELETE', `template/${templateId}`, request.toJSON());
        return response;
    }

    /**
     * Get templates list.
     * The tables paginate on the client over the whole list, so when the caller
     * asks for the first page we keep pulling the remaining pages and return them
     * merged; otherwise accounts with more than `size` templates silently lose the
     * tail of the list. The merged content is sorted newest first, since the
     * backend returns it in no particular order.
     * @param {string} flowType - Flow type
     * @param {number} page - Page number
     * @param {number} size - Page size
     * @param {boolean} fetchAll - Follow the remaining pages (only from page 0)
     * @returns {Promise<PageTemplateDto>}
     */
    async getTemplates(flowType, page = 0, size = 100, fetchAll = true) {
        const response = await this.apiRequest('GET', 'template', null, {
            flowType,
            page,
            size,
        });

        const content = [...(response.data.content || [])];
        const totalPages = response.data.totalPages || 0;

        if (fetchAll && page === 0 && totalPages > 1) {
            for (let nextPage = 1; nextPage < totalPages; nextPage += 1) {
                const nextResponse = await this.apiRequest('GET', 'template', null, {
                    flowType,
                    page: nextPage,
                    size,
                });
                content.push(...(nextResponse.data.content || []));
            }
        }

        const mergedExtraPages = content.length !== response.data.numberOfElements;

        // The backend returns no particular order: newest first, so a freshly created
        // template is always the first row (and the default preview).
        const createdAt = (template) =>
            Number(template.date_created || template.created_at || template.createdAt || template.dateCreated || 0);
        content.sort((a, b) => createdAt(b) - createdAt(a));

        return new PageTemplateDto.Builder()
            .withTotalElements(response.data.totalElements)
            .withTotalPages(response.data.totalPages)
            .withFirst(response.data.first)
            .withLast(mergedExtraPages ? true : response.data.last)
            .withNumberOfElements(content.length)
            .withSize(response.data.size)
            .withContent(content)
            .withNumber(response.data.number)
            .withEmpty(content.length === 0)
            .build();
    }

    /**
     * Create template
     * @param {TemplateRequestDto} request - Template request data
     * @returns {Promise<Object>}
     */
    async createTemplate(request) {
        const response = await this.apiRequest('POST', 'template', request);
        return response;
    }

    /**
     * Get template variables
     * @param {string} skillId - Skill ID
     * @param {string} flowType - Flow type
     * @param {string} language - Language code
     * @returns {Promise<TemplateVariablesResponseDto>}
     */
    async getTemplateVariables(skillId, flowType, language) {
        const response = await this.apiRequest('GET', 'template-variables', null, {
            skillId,
            flowType,
            language,
        });
        return new TemplateVariablesResponseDto.Builder()
            .withUuid(response.data.uuid)
            .withTemplateType(response.data.template_type)
            .withFlowType(response.data.flow_type)
            .withSkillId(response.data.skill_id)
            .withVariables(response.data.variables)
            .withContentExample(response.data.content_example)
            .withLanguage(response.data.language)
            .withDateCreated(response.data.date_created)
            .withDateUpdated(response.data.date_updated)
            .build();
    }

    /**
     * Create template variables
     * @param {TemplateVariablesDto} request - Template variables request data
     * @returns {Promise<TemplateVariablesDto>}
     */
    async createTemplateVariables(request) {
        const response = await this.apiRequest('POST', 'template-variables', request.toJSON());
        return new TemplateVariablesDto.Builder()
            .withUuid(response.data.uuid)
            .withSkillId(response.data.skill_id)
            .withTemplateType(response.data.template_type)
            .withFlowType(response.data.flow_type)
            .withVariables(response.data.variables)
            .withLanguage(response.data.language)
            .withContentExample(response.data.content_example)
            .build();
    }

    /**
     * Get available languages
     * @returns {Promise<Object>}
     */
    async getAvailableLanguages() {
        const response = await this.apiRequest('GET', 'languages');
        const languages = response.data.map((language) => ({
            tag: language.tag,
            name: language.name,
        }));
        return languages;
    }

    /**
     * Create checkout scheduler
     * @param {string} skillId - Skill ID
     * @param {CheckoutSchedulerDto} request - Checkout scheduler request data
     * @returns {Promise<Object>}
     */
    async createCheckoutScheduler(skillId, request) {
        const response = await this.apiRequest('POST', `skill/${skillId}/checkout-scheduler`, request);
        return response;
    }

    /**
     * Get checkout scheduler
     * @param {string} skillId - Skill ID
     * @returns {Promise<CheckoutSchedulerDto>}
     */
    /**
     * @returns {Promise<{scheduler: CheckoutSchedulerDto, configured: boolean}>} `configured` es si
     *     la cuenta tiene cron: sin él la skill puede estar encendida y no enviar nada.
     */
    async getCheckoutScheduler(skillId) {
        const response = await this.apiRequest('GET', `skill/${skillId}/checkout-scheduler`);
        const scheduler = new CheckoutSchedulerDto.Builder()
            .withTtlMinutes(response.data.ttl_minutes)
            .withSteps(response.data.steps)
            .build();
        // Backends anteriores no mandan `configured`: con ellos un 200 siempre era un cron.
        return { scheduler, configured: response.data.configured !== false };
    }
}

export default new TemplatesApiRepository();
